import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
    SimpleGrid,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '@chakra-ui/icons';
import React, { useEffect } from 'react';
import { FaExclamationTriangle, FaHistory, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Card from 'components/card/Card';
import CustomSelect from 'components/select/CustomSelect';
import { useGetSearchLogsQuery, useGetSearchLogStatsQuery } from 'api/searchLogsSlice';

const PER_PAGE = 20;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const SearchLog = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [source, setSource] = React.useState('');
  const [emptyOnly, setEmptyOnly] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageLoading, setPageLoading] = React.useState(true);
  const requestKey = `${currentPage}|${searchQuery}|${source}|${emptyOnly}`;

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const searchBg = useColorModeValue('secondaryGray.300', 'gray.700');
  const searchColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const cardBg = useColorModeValue('white', 'navy.800');

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      if (next === searchQuery) return;
      setSearchQuery(next);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery]);

  useEffect(() => {
    setPageLoading(true);
  }, [requestKey]);

  const { data: statsResponse } = useGetSearchLogStatsQuery();
  const { data, isLoading, isError, isFetching } = useGetSearchLogsQuery(
    {
      search: searchQuery,
      source,
      empty_only: emptyOnly,
      page: currentPage,
      per_page: PER_PAGE,
    },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (!isFetching && !isLoading) setPageLoading(false);
  }, [isFetching, isLoading, requestKey, data]);

  const stats = statsResponse?.data || {};
  const items = Array.isArray(data?.data) ? data.data : [];
  const pagination = data?.pagination || {};
  const total = pagination.total || 0;
  const lastPage = pagination.last_page || 1;
  const from = pagination.from || 0;
  const to = pagination.to || 0;
  const showShimmer = isLoading || pageLoading;

  return (
    <Box className="nr-page">
      <Flex mb="20px" justify="space-between" align="center" wrap="wrap" gap={3}>
        <Box>
          <Text color={textColor} fontSize="22px" fontWeight="700">
            Search Log
          </Text>
          <Text color={muted} fontSize="sm" mt="4px">
            See what users search for and which remedies may be missing.
          </Text>
        </Box>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} mb="20px">
        <Card p="20px">
          <Text color={muted} fontSize="sm">Total searches</Text>
          <Text color={textColor} fontSize="28px" fontWeight="700">
            {stats.total_searches ?? 0}
          </Text>
        </Card>
        <Card p="20px">
          <Text color={muted} fontSize="sm">Unique queries</Text>
          <Text color={textColor} fontSize="28px" fontWeight="700">
            {stats.unique_queries ?? 0}
          </Text>
        </Card>
        <Card p="20px">
          <Text color={muted} fontSize="sm">No results</Text>
          <Text color="red.500" fontSize="28px" fontWeight="700">
            {stats.empty_searches ?? 0}
          </Text>
        </Card>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5} mb="20px">
        <Card p="20px">
          <Flex align="center" gap={2} mb="14px">
            <Icon as={FaSearch} color="brand.500" />
            <Text color={textColor} fontWeight="700">Most searched</Text>
          </Flex>
          {(stats.top_queries || []).length === 0 ? (
            <Text color={muted} fontSize="sm">No searches logged yet.</Text>
          ) : (
            (stats.top_queries || []).slice(0, 10).map((item) => (
              <Flex key={item.query} justify="space-between" py="8px" borderBottomWidth="1px" borderColor={borderColor}>
                <Text color={textColor} fontWeight="600" noOfLines={1}>{item.query}</Text>
                <Badge colorScheme={item.min_results === 0 ? 'red' : 'green'}>
                  {item.times}× · {item.min_results} hits
                </Badge>
              </Flex>
            ))
          )}
        </Card>

        <Card p="20px">
          <Flex align="center" gap={2} mb="14px">
            <Icon as={FaExclamationTriangle} color="orange.400" />
            <Text color={textColor} fontWeight="700">Needs a remedy</Text>
          </Flex>
          {(stats.missing_remedies || []).length === 0 ? (
            <Text color={muted} fontSize="sm">No empty searches yet.</Text>
          ) : (
            (stats.missing_remedies || []).slice(0, 10).map((item) => (
              <Flex key={item.query} justify="space-between" align="center" py="8px" borderBottomWidth="1px" borderColor={borderColor} gap={3}>
                <Box>
                  <Text color={textColor} fontWeight="600">{item.query}</Text>
                  <Text color={muted} fontSize="xs">{item.times} searches · last {formatDate(item.last_searched)}</Text>
                </Box>
                <Button size="xs" colorScheme="green" onClick={() => navigate(`/admin/add-remedy?title=${encodeURIComponent(item.query)}`)}>
                  Add remedy
                </Button>
              </Flex>
            ))
          )}
        </Card>
      </SimpleGrid>

      <Card flexDirection="column" w="100%" px="0px">
        <Flex
          px={{ base: '16px', md: '25px' }}
          mb="16px"
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          gap={3}
        >
          <Flex align="center" gap={2}>
            <Icon as={FaHistory} />
            <Text color={textColor} fontSize="18px" fontWeight="700">Recent searches</Text>
          </Flex>
          <Flex gap={3} wrap="wrap">
            <InputGroup w={{ base: '100%', md: '280px' }}>
              <InputLeftElement>
                <IconButton bg="inherit" borderRadius="inherit" _hover="none" icon={<SearchIcon w="15px" h="15px" />} />
              </InputLeftElement>
              <Input
                variant="search"
                fontSize="sm"
                bg={searchBg}
                color={searchColor}
                borderRadius="30px"
                placeholder="Filter queries..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </InputGroup>
            <CustomSelect
              w="160px"
              value={source}
              onChange={(e) => {
                setSource(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All sources</option>
              <option value="remedy">Remedies</option>
              <option value="article">Articles</option>
              <option value="video">Videos</option>
              <option value="course">Courses</option>
              <option value="ai">AI search</option>
            </CustomSelect>
            <Button
              variant={emptyOnly ? 'solid' : 'outline'}
              colorScheme="red"
              size="sm"
              onClick={() => {
                setEmptyOnly((prev) => !prev);
                setCurrentPage(1);
              }}
            >
              No results only
            </Button>
          </Flex>
        </Flex>

        <Box px={{ base: '16px', md: '25px' }} pb="24px" overflowX="auto">
          {isError ? (
            <Text color="red.500">Could not load search logs.</Text>
          ) : showShimmer ? (
            <Box>
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} h="36px" mb="10px" borderRadius="md" />
              ))}
            </Box>
          ) : items.length === 0 ? (
            <Text color={muted}>No search history yet. It will appear as users search in the app.</Text>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th color="gray.400" borderColor={borderColor}>Query</Th>
                  <Th color="gray.400" borderColor={borderColor}>Source</Th>
                  <Th color="gray.400" borderColor={borderColor}>Results</Th>
                  <Th color="gray.400" borderColor={borderColor}>User</Th>
                  <Th color="gray.400" borderColor={borderColor}>When</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((item) => (
                  <Tr key={item.id} bg={item.result_count === 0 ? 'red.50' : cardBg}>
                    <Td borderColor={borderColor}>
                      <Text fontWeight="700" color={textColor}>{item.query}</Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Badge>{item.source}</Badge>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Badge colorScheme={item.result_count === 0 ? 'red' : 'green'}>
                        {item.result_count}
                      </Badge>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Text fontSize="sm" color={textColor}>
                        {item.user_name || item.user_email || 'Guest'}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Text fontSize="sm" color={muted}>{formatDate(item.created_at)}</Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}

          {total > 0 && (
            <Flex justify="space-between" align="center" mt="20px" wrap="wrap" gap={3}>
              <Text fontSize="sm" color={muted}>Showing {from}-{to} of {total}</Text>
              <Flex align="center" gap={3}>
                <IconButton
                  icon={<ChevronLeftIcon />}
                  size="sm"
                  variant="outline"
                  colorScheme="brand"
                  isDisabled={currentPage <= 1 || isFetching}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                />
                <Text fontSize="sm" color={textColor}>Page {currentPage} of {lastPage}</Text>
                <IconButton
                  icon={<ChevronRightIcon />}
                  size="sm"
                  variant="outline"
                  colorScheme="brand"
                  isDisabled={currentPage >= lastPage || isFetching}
                  onClick={() => setCurrentPage((page) => Math.min(lastPage, page + 1))}
                />
              </Flex>
            </Flex>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default SearchLog;
